// Routing and the two gates.
//
// Every signed-in route sits behind both gates in order: authenticated, then vault-unlocked. The
// gates are components rather than checks sprinkled through screens, so it isn't possible to add a
// screen later that renders journal content without a key.

import { useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from './auth/SessionProvider';
import { AppShell } from './components/AppShell';
import { FullPageSpinner, Notice } from './components/ui';
import { SignInScreen } from './screens/SignInScreen';
import { ResetPasswordScreen } from './screens/ResetPasswordScreen';
import { VaultSetupScreen } from './screens/VaultSetupScreen';
import { VaultUnlockScreen } from './screens/VaultUnlockScreen';
import { PersonalPractice } from './components/PersonalPractice';
import { HomeScreen } from './screens/HomeScreen';
import { JournalScreen } from './screens/JournalScreen';
import { ComposerScreen } from './screens/ComposerScreen';
import { OracleScreen } from './screens/OracleScreen';
import { SelfScreen } from './screens/SelfScreen';
import { ReviewScreen } from './self/ReviewScreen';
import { ArchiveScreen } from './self/ArchiveScreen';
import { MoodRitualScreen } from './screens/MoodRitualScreen';
import { CheckInDeckScreen } from './screens/CheckInDeckScreen';
import { MilestonesScreen } from './self/MilestonesScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { PricingScreen } from './screens/PricingScreen';
import { DiscoverScreen } from './screens/DiscoverScreen';
import { MirrorScreen } from './screens/MirrorScreen';
import { SymbolDetailScreen, SymbolVaultScreen } from './screens/SymbolScreens';
import { ThreadsScreen } from './threads/ThreadsScreen';
import { DreamsScreen } from './screens/DreamsScreen';
import { RequireOnboarding } from './onboarding/RequireOnboarding';
import { OnboardingFlow } from './onboarding/OnboardingFlow';
import { JournalEntryFlow } from './screens/JournalEntryFlow';
import { EntriesProvider } from './entries/EntriesProvider';
import { JournalsProvider } from './journals/JournalsProvider';
import { JournalsShelf } from './journals/JournalsShelf';
import { JournalDetailScreen } from './journals/JournalDetailScreen';
import { NewJournalScreen } from './journals/NewJournalScreen';

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      {/* Public on purpose: the whole point is that the person cannot sign in. Must stay outside
          RequireAuth, or the emailed link would bounce to the login screen it exists to escape. */}
      <Route path="/reset-password" element={<ResetPasswordScreen />} />
      {/* Dev only. Onboarding sits behind auth and an open vault, so without this the only way to
          look at a change to it is to make a fresh account. Stripped from the production bundle:
          import.meta.env.DEV is replaced by `false` at build time. */}
      {import.meta.env.DEV && (
        <Route path="/preview/onboarding" element={<EntriesProvider><OnboardingFlow onDone={() => {}} /></EntriesProvider>} />
      )}
      <Route
        path="/*"
        element={
          <RequireAuth>
            <RequireVault>
              <EntriesProvider>
              <RequireOnboarding>
                {/* Journals are a view over entries, so the shelf sits inside the entry state it
                    resolves its membership ids against. */}
                <JournalsProvider>
                <AppShell>
                  <Routes>
                    <Route index element={<HomeScreen />} />
                    <Route path="journal" element={<JournalScreen />} />
                    {/* A second way of looking at the same writing. /journal is untouched. */}
                    <Route path="journals" element={<JournalsShelf />} />
                    <Route path="journals/new" element={<NewJournalScreen />} />
                    <Route path="journals/:journalId" element={<JournalDetailScreen />} />
                    <Route path="oreia" element={<OracleScreen />} />
                    {/* A thread has its own address once it has been spoken into, so a reload keeps
                        it and a conversation can be linked to. Bare /oreia stays the new one. */}
                    <Route path="oreia/:conversationId" element={<OracleScreen />} />
                    <Route path="self" element={<SelfScreen />} />
                    <Route path="self/mood" element={<MoodRitualScreen />} />
                    <Route path="self/check-ins" element={<CheckInDeckScreen />} />
                    <Route path="self/review" element={<ReviewScreen />} />
                    <Route path="self/archive" element={<ArchiveScreen />} />
                    <Route path="self/milestones" element={<MilestonesScreen />} />
                    <Route path="practice" element={<PersonalPractice full />} />
                    <Route path="discover" element={<DiscoverScreen />} />
                    <Route path="mirror" element={<MirrorScreen />} />
                    <Route path="dreams" element={<DreamsScreen />} />
                    <Route path="symbols" element={<SymbolVaultScreen />} />
                    <Route path="symbols/:name" element={<SymbolDetailScreen />} />
                    {/* Every dream↔waking connection ever found. The dashboard card shows two. */}
                    <Route path="threads" element={<ThreadsScreen />} />
                    <Route path="settings" element={<SettingsScreen />} />
                    <Route path="pricing" element={<PricingScreen />} />
                    {/* The composer is rendered by the sibling <Routes> below as a full-screen
                        overlay. It still has to be matched here, or the catch-all would treat
                        /write as unknown and redirect to Home before the sheet ever appears. */}
                    <Route path="write/*" element={null} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </AppShell>
                {/* The composer is a full-screen sheet layered over whatever is behind it, the
                    same way it works on iPhone. */}
                <Routes>
                  <Route path="write" element={<ComposerScreen />} />
                  <Route path="write/conversation" element={<JournalEntryFlow />} />
                  <Route path="write/:entryId" element={<ComposerScreen />} />
                </Routes>
                </JournalsProvider>
              </RequireOnboarding>
              </EntriesProvider>
            </RequireVault>
          </RequireAuth>
        }
      />
    </Routes>
  );
}

/**
 * The Google OAuth flow finishes by redirecting to /login?token=…. Swap that token for a session
 * and scrub it from the URL so it doesn't sit in history or get pasted into a bug report.
 */
function LoginRoute() {
  const { user, isLoading, adoptToken } = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  useEffect(() => {
    if (!token) return;
    void adoptToken(token).finally(() => navigate('/', { replace: true }));
  }, [token, adoptToken, navigate]);

  if (token || isLoading) return <FullPageSpinner label="Signing you in…" />;
  if (user) return <Navigate to="/" replace />;
  return <SignInScreen />;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading } = useSession();
  if (isLoading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireVault({ children }: { children: ReactNode }) {
  const { vault } = useSession();

  switch (vault.status) {
    case 'loading':
      return <FullPageSpinner label="Opening your vault…" />;
    // Both stay on the setup screen: 'needs-backup' means the key exists but the recovery phrase
    // hasn't been acknowledged, and it can never be shown again once we leave.
    case 'needs-setup':
    case 'needs-backup':
      return <VaultSetupScreen />;
    case 'locked':
      return <VaultUnlockScreen />;
    case 'error':
      return (
        <div className="oscreen">
          <div className="oscroll">
            <div className="ocolumn opane">
              <h1 className="oserif" style={{ fontSize: 34, margin: '0 0 14px' }}>
                We couldn't reach your vault.
              </h1>
              <Notice tone="danger">{vault.message}</Notice>
              <button className="octa" style={{ marginTop: 20 }} onClick={() => window.location.reload()}>
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    case 'unlocked':
      return <>{children}</>;
  }
}
